<template>
	<div class="stock-main-data">
		<div v-if="mainData.length" class="main-data-wrapper">
			<div v-for="(item, index) in mainData" :key="index" class="main-data-row">
				<div
					v-for="(data, idx) in item"
					:key="idx"
					class="main-data-col"
					:class="[data.unitStyle ? `main-data-col--${data.unitStyle}` : 'main-data-col--up']"
				>
					<div class="main-data-col-label">{{ data.label }}</div>
					<div class="b-flex-baseline">
						<div class="main-data-col-value">{{ data.value }}</div>
						<div v-if="data.unit" class="main-data-col-unit">{{ data.unit }}</div>
						<RiseFallRange v-if="data.unitStyle" size="8" :type="data.unitStyle" class="ml-4" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';
import RiseFallRange from '@/components/RiseFallRange.vue';
import { convertNumberToCNUnit } from '@/utils/api';
import { EN } from '@/locales';
import { useStockVO } from '@rebuild/hooks/useStockVO';

const props = defineProps({
	stockQuoteData: {
		type: Object,
		default: () => ({})
	},
	market: {
		type: String,
		default: ''
	},
	isPeTtm: {
		type: Boolean,
		default: false
	},
	isEyebright: {
		type: Boolean,
		default: false
	}
});

const { proxy } = getCurrentInstance();
const { isHKStock: globalIsHKStock, isUSStock: globalIsUSStock } = useStockVO();

// 当前定制模块优先使用传入的市场，避免依赖全局股票选择状态。
const isHKStock = computed(() => {
	return props.market ? props.market.toUpperCase() === 'HK' : globalIsHKStock.value;
});
const isUSStock = computed(() => {
	return props.market ? props.market.toUpperCase() === 'US' : globalIsUSStock.value;
});

const mainData = ref([]);

const initMainData = result => {
	const dataCopy = JSON.parse(JSON.stringify(result));
	let rowArray = [
		[
			{ label: proxy.$t('enterprise.chg'), prop: 'change' },
			{ label: proxy.$t('enterprise.%Chg'), prop: 'changePct' },
			{ label: proxy.$t('enterprise.high'), prop: 'highPrice' },
			{ label: proxy.$t('enterprise.low'), prop: 'lowPrice' }
		],
		[
			{ label: proxy.$t('enterprise.turnover'), prop: 'turnoverValue' },
			{
				label: props.isPeTtm ? proxy.$t('enterprise.clear-P/E') : proxy.$t('enterprise.P/E'),
				prop: 'peTtm'
			},
			{ label: proxy.$t('enterprise.market-cap'), prop: 'totalMv' }
		],
		[
			{
				label: proxy.$t('enterprise.volume'),
				prop: isHKStock.value ? 'turnoverVolumeShares' : 'turnoverVolume'
			},
			{ label: proxy.$t('enterprise.P/B'), prop: 'pb' },
			{ label: proxy.$t('enterprise.float-cap'), prop: 'negotiableMv' }
		]
	];

	if (isUSStock.value) {
		delete rowArray[2];
		rowArray[1][0] = { label: proxy.$t('enterprise.volume'), prop: 'turnoverVolumeShares' };
	}

	if (props.isEyebright) {
		rowArray = rowArray.map(row => {
			const copy = [...row];
			while (copy.length % 4 !== 0) copy.push({ empty: true });
			return copy;
		});
	}

	const formatValue = (prop, value) => (prop === 'changePct' ? `${value}%` : value);
	const formatUnit = (prop, unit) => {
		if (prop === 'turnoverVolume') return proxy.$i18n.locale === EN ? unit : unit + '手';
		if (prop === 'turnoverVolumeShares') return proxy.$i18n.locale === EN ? unit : unit + '股';
		return unit;
	};
	const formatUnitStyle = (prop, value) => {
		if (prop === 'change' || prop === 'changePct') return value > 0 ? 'up' : 'down';
		return null;
	};

	mainData.value = rowArray.filter(Boolean).map(row =>
		row.map(item => {
			let displayValue = dataCopy[item.prop];
			if (proxy.$i18n.locale === EN && item.prop === 'turnoverVolume') displayValue *= 100;
			const { value, unit } = convertNumberToCNUnit(displayValue);

			return {
				label: item.empty ? '' : item.label,
				value: item.empty ? '' : formatValue(item.prop, value),
				unit: formatUnit(item.prop, unit),
				unitStyle: formatUnitStyle(item.prop, value)
			};
		})
	);
};

watch(
	[() => props.stockQuoteData, () => props.market],
	([newData]) => {
		if (newData && Object.keys(newData).length) initMainData(newData);
	},
	{ deep: true, immediate: true }
);

watch(
	() => proxy.$i18n.locale,
	() => {
		if (props.stockQuoteData && Object.keys(props.stockQuoteData).length) {
			proxy.$nextTick(() => initMainData(props.stockQuoteData));
		}
	}
);

defineExpose({ initMainData });
</script>

<style lang="less" scoped>
.stock-main-data {
	.main-data-row {
		display: flex;

		& + .main-data-row {
			margin-top: 56px;
		}
	}
	.main-data-col {
		flex: none;
		width: 121px;
		position: relative;

		&:not(:last-child)::after {
			content: '';
			position: absolute;
			top: 6px;
			right: 0;
			bottom: 6px;
			width: 1px;
			background: #c9cdd4;
		}

		& + .main-data-col {
			margin-left: 37px;
		}
		&--up {
			--unit-style-color: #fa4646;
		}
		&--down {
			--unit-style-color: #30bf54;
		}
		&-label {
			color: #4e5969;
			font-size: 16px;
			font-weight: 400;
			line-height: 140%;
		}
		&-value {
			margin-top: 8px;
			color: #333;
			font-family: Barlow;
			font-size: 24px;
			font-weight: 500;
			line-height: normal;
			white-space: nowrap;
		}
		&-unit {
			margin-left: 2px;
			color: #333;
			font-size: 14px;
			font-weight: 400;
			line-height: 20px;
		}
	}
	.main-data-wrapper {
		margin-top: 48px;
	}
}
</style>
